import React, { useEffect, useMemo, useState } from 'react';
import { addDays, endOfWeek, format, isWithinInterval, parseISO, startOfWeek, subWeeks, addWeeks } from 'date-fns';
import { planVsActualReportApi, employeeApi, payRecordApi, scheduleApi, shiftApi, timecardApi, type PlanVsActualReportEmployeeRow, type PlanVsActualWeekReportResponse } from '../services/api';
import { Employee, PayRecord, Schedule, Shift, Timecard, TimecardDay } from '../types';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useLocationContext } from '../components/Layout';
import { WeekNavigator } from '../components/WeekNavigator';
import { parseLocalDate } from '../utils/payrollPeriodLocal';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { buildActivePayRecordMap, getShiftLaborCost, resolveWorkHourlyRate } from '../utils/laborCost';

type ScheduleFilter = number | 'all' | 'unscheduled';

function mapReportEmployees(rows: PlanVsActualReportEmployeeRow[]): Employee[] {
  return rows.map(row => ({
    id: row.id,
    firstName: row.firstName,
    lastName: row.lastName,
    isActive: row.isActive,
    locationId: row.mainLocationId,
    profile: { mainLocationId: row.mainLocationId },
  } as Employee));
}

function applyWeekReportData(
  data: PlanVsActualWeekReportResponse,
  setters: {
    setSchedules: (v: Schedule[]) => void;
    setShifts: (v: Shift[]) => void;
    setTimecards: (v: Timecard[]) => void;
    setPayRecords: (v: PayRecord[]) => void;
    setEmployees: (v: Employee[]) => void;
  }
) {
  setters.setSchedules(data.schedules.filter(schedule => schedule.isActive));
  setters.setShifts(data.shifts);
  setters.setTimecards(data.timecards);
  setters.setPayRecords(data.payRecords);
  setters.setEmployees(mapReportEmployees(data.employees));
}

async function loadLegacyWeekReport(params: {
  apiWeekStart: Date;
  weekStart: Date;
  weekEnd: Date;
  locationId?: number;
}): Promise<PlanVsActualWeekReportResponse> {
  const weekStartStr = format(params.apiWeekStart, 'yyyy-MM-dd');
  const weekStartDisplay = format(params.weekStart, 'yyyy-MM-dd');
  const weekEndDisplay = format(params.weekEnd, 'yyyy-MM-dd');
  const [scheduleData, payRecordData, employeeData, timecardData, shiftData] = await Promise.all([
    scheduleApi.getAll(),
    payRecordApi.getAll(),
    employeeApi.getAll(),
    timecardApi.getAll(undefined, weekStartStr, params.locationId),
    shiftApi.getAll({
      startDate: weekStartDisplay,
      endDate: weekEndDisplay,
      ...(params.locationId ? { locationId: params.locationId } : {}),
    }),
  ]);

  const employeeIds = new Set<number>([
    ...shiftData.map(s => s.employeeId),
    ...timecardData.map(t => t.employeeId),
  ]);

  return {
    weekStartDate: weekStartStr,
    weekStartDisplay,
    weekEndDisplay,
    locationId: params.locationId ?? null,
    schedules: scheduleData.filter(s => s.isActive),
    shifts: shiftData,
    timecards: timecardData,
    payRecords: payRecordData.filter(pr => pr.employeeId != null && employeeIds.has(pr.employeeId)),
    employees: employeeData
      .filter(e => employeeIds.has(e.id))
      .map(e => ({
        id: e.id,
        firstName: e.firstName,
        lastName: e.lastName,
        isActive: e.isActive,
        mainLocationId:
          e.locationId ??
          e.profile?.mainLocationId ??
          e.profile?.mainLocation?.id ??
          e.location?.id ??
          0,
      })),
  };
}

const getTwoLetterCode = (name: string | null | undefined): string => {
  if (!name) return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(word => word.length > 0);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  const word = words[0].toUpperCase();
  return word.length >= 2 ? word.substring(0, 2) : word.padEnd(2, 'X');
};

const getScheduledHours = (day: TimecardDay): number => {
  if (!day.scheduledIn || !day.scheduledOut) {
    return 0;
  }
  const start = parseISO(day.scheduledIn);
  const end = parseISO(day.scheduledOut);
  let totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
  // Overnight shift: ScheduledOut earlier than ScheduledIn
  if (totalMinutes <= 0) {
    totalMinutes += 24 * 60;
  }
  const breakPaid = day.paidBreakPaid ?? false;
  if (!breakPaid) {
    totalMinutes -= day.scheduledBreakMinutes ?? 0;
  }
  return Math.round(Math.max(0, totalMinutes / 60) * 100) / 100;
};

const getActualPaidHours = (day: TimecardDay): number => {
  if (day.totalHours !== null && day.totalHours !== undefined) {
    return Number(day.totalHours) || 0;
  }
  return 0;
};

const timecardDayCost = (pr: PayRecord | undefined, day: TimecardDay, locationId?: number | null): number =>
  getActualPaidHours(day) * resolveWorkHourlyRate(pr, {
    locationId,
    positionId: day.positionId,
    scheduleId: day.scheduleId,
  });

/** True if this timecard day has no shift associated (no scheduled in/out). */
const isDayUnScheduled = (day: TimecardDay): boolean =>
  !day.scheduledIn || !day.scheduledOut;

/** Plan hours from a shift (paid hours, or derived from start/end). */
const getShiftHours = (shift: Shift): number => {
  if (shift.paidHours != null && Number(shift.paidHours) >= 0) {
    return Number(shift.paidHours);
  }
  try {
    const start = parseISO(shift.startTime);
    const end = parseISO(shift.endTime);
    let minutes = (end.getTime() - start.getTime()) / (1000 * 60);
    if (minutes <= 0) minutes += 24 * 60;
    const unpaid = (shift.unpaidHours ?? 0) * 60;
    return Math.round(Math.max(0, (minutes - unpaid) / 60) * 100) / 100;
  } catch {
    return 0;
  }
};

/** Colors for schedule segments in All Schedules Breakdown ring. */
const SCHEDULE_SEGMENT_COLORS = [
  '#60a5fa', '#34d399', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
  '#f97316', '#6366f1', '#14b8a6', '#eab308', '#a855f7', '#d946ef', '#0ea5e9', '#22c55e'
];

const getEmployeeLocationId = (employee: Employee): number | null => {
  return (
    employee.locationId ??
    employee.profile?.mainLocationId ??
    employee.profile?.mainLocation?.id ??
    employee.location?.id ??
    null
  );
};

/** Segment for All Schedules Breakdown ring (schedule name + hours + cost + color). */
type CircleSegment = { scheduleId: number; name: string; hours: number; cost: number; color: string };

/** Build SVG path for a donut segment (angles in radians, 0 = 3 o'clock, clockwise). */
function donutSegmentPath(cx: number, cy: number, innerR: number, outerR: number, startAngle: number, endAngle: number): string {
  const fullCircle = 2 * Math.PI;
  const span = endAngle - startAngle;
  if (span >= fullCircle - 1e-9) {
    endAngle = startAngle + fullCircle - 1e-5;
  }
  const x1 = cx + outerR * Math.cos(startAngle);
  const y1 = cy + outerR * Math.sin(startAngle);
  const x2 = cx + outerR * Math.cos(endAngle);
  const y2 = cy + outerR * Math.sin(endAngle);
  const x3 = cx + innerR * Math.cos(endAngle);
  const y3 = cy + innerR * Math.sin(endAngle);
  const x4 = cx + innerR * Math.cos(startAngle);
  const y4 = cy + innerR * Math.sin(startAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${large} 0 ${x4} ${y4} Z`;
}

const CircleCard: React.FC<{
  title: string;
  hours: number;
  cost?: number;
  subtitle: string;
  totalHours?: number;
  highlightHours?: number;
  lightColor: string;
  darkColor: string;
  /** When set, render ring as segments (one per schedule); totalHours/highlightHours ignored. */
  segments?: CircleSegment[];
}> = ({ title, hours, cost, subtitle, totalHours, highlightHours, lightColor, darkColor, segments }) => {
  const [hoveredSegment, setHoveredSegment] = useState<CircleSegment | null>(null);
  const totalSegHours = segments?.reduce((s, seg) => s + seg.hours, 0) ?? 0;
  const ringStyle: React.CSSProperties =
    !segments || segments.length === 0 || totalSegHours <= 0
      ? totalHours && highlightHours !== undefined && totalHours > 0
        ? { background: `conic-gradient(${darkColor} 0 ${Math.min(100, (highlightHours / totalHours) * 100)}%, ${lightColor} ${Math.min(100, (highlightHours / totalHours) * 100)}% 100%)` }
        : { background: lightColor }
      : {};

  const cx = 50;
  const cy = 50;
  const innerR = 28;
  const outerR = 40;
  const startAngle = -Math.PI / 2;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-sm font-semibold text-neutral-700">{title}</div>
      <div className="relative w-64 h-64 rounded-full p-3">
        {segments && segments.length > 0 && totalSegHours > 0 ? (
          <svg viewBox="0 0 100 100" className="w-full h-full rounded-full" style={{ transform: 'rotate(0deg)' }}>
            {segments.map((seg, i) => {
              const segStart = startAngle + (segments.slice(0, i).reduce((a, b) => a + b.hours, 0) / totalSegHours) * 2 * Math.PI;
              const segEnd = segStart + (seg.hours / totalSegHours) * 2 * Math.PI;
              const path = donutSegmentPath(cx, cy, innerR, outerR, segStart, segEnd);
              const isHovered = hoveredSegment?.scheduleId === seg.scheduleId;
              return (
                <path
                  key={seg.scheduleId}
                  d={path}
                  fill={seg.color}
                  stroke={isHovered ? '#1e3a5f' : 'transparent'}
                  strokeWidth={1.5}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredSegment(seg)}
                  onMouseLeave={() => setHoveredSegment(null)}
                />
              );
            })}
          </svg>
        ) : (
          <div className="w-full h-full rounded-full" style={ringStyle} />
        )}
        {hoveredSegment && segments && segments.length > 0 && totalSegHours > 0 && (() => {
          const idx = segments.findIndex(s => s.scheduleId === hoveredSegment.scheduleId);
          if (idx < 0) return null;
          let segStart = startAngle + (segments.slice(0, idx).reduce((a, b) => a + b.hours, 0) / totalSegHours) * 2 * Math.PI;
          const segEnd = segStart + (segments[idx].hours / totalSegHours) * 2 * Math.PI;
          const midAngle = (segStart + segEnd) / 2;
          const tipRadius = 58;
          const tipX = 50 + tipRadius * Math.cos(midAngle);
          const tipY = 50 + tipRadius * Math.sin(midAngle);
          return (
            <div
              className="absolute z-10 pointer-events-none"
              style={{
                left: `${tipX}%`,
                top: `${tipY}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="bg-neutral-900/95 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                <div className="font-semibold truncate max-w-[140px]" title={hoveredSegment.name}>{hoveredSegment.name}</div>
                <div>{hoveredSegment.hours.toFixed(2)}h</div>
                <div>€{hoveredSegment.cost.toFixed(2)}</div>
              </div>
            </div>
          );
        })()}
        <div
          className={`absolute rounded-full bg-white flex flex-col items-center justify-center text-center pointer-events-none ${segments && segments.length > 0 && totalSegHours > 0 ? 'left-[22%] top-[22%] w-[56%] h-[56%]' : 'inset-5'}`}
        >
          <div className="text-2xl font-bold text-neutral-900">{hours.toFixed(2)}h</div>
          <div className="text-sm text-neutral-500">{subtitle}</div>
          {cost !== undefined && (
            <div className="text-base font-semibold text-neutral-800 mt-1">€{cost.toFixed(2)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlanVsActual: React.FC = () => {
  const { debouncedSelectedLocationId } = useLocationContext();
  const weekStartsOn = useWeekStartDay();
  const [currentWeek, setCurrentWeek] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn })
  );
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<ScheduleFilter>('all');
  const [allSchedulesBreakdown, setAllSchedulesBreakdown] = useState(false);
  const [timecards, setTimecards] = useState<Timecard[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [payRecords, setPayRecords] = useState<PayRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('Preparing report...');
  const [error, setError] = useState<string | null>(null);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const isBusy = loading;

  // Backend timecards always use Monday as week start; use that for the API regardless of display week start.
  const apiWeekStart = useMemo(
    () => startOfWeek(weekStart, { weekStartsOn: 1 }),
    [weekStart]
  );

  useEffect(() => {
    let cancelled = false;
    let progressTimer: ReturnType<typeof setInterval> | null = null;

    const loadReport = async () => {
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      setLoadingStage('Loading report data...');

      let simulated = 0;
      progressTimer = setInterval(() => {
        if (cancelled) return;
        simulated = Math.min(
          90,
          simulated + (simulated < 40 ? 4 : simulated < 70 ? 2 : 1)
        );
        setLoadingProgress(simulated);
      }, 120);

      try {
        let data: PlanVsActualWeekReportResponse;
        try {
          data = await planVsActualReportApi.getWeekReport({
            weekStartDate: format(apiWeekStart, 'yyyy-MM-dd'),
            weekStartDisplay: format(weekStart, 'yyyy-MM-dd'),
            weekEndDisplay: format(weekEnd, 'yyyy-MM-dd'),
            locationId: debouncedSelectedLocationId ?? undefined,
          });
        } catch (aggregateErr) {
          console.warn('Aggregate Plan vs Actual API failed; using legacy loaders.', aggregateErr);
          setLoadingStage('Retrying with legacy loaders...');
          data = await loadLegacyWeekReport({
            apiWeekStart,
            weekStart,
            weekEnd,
            locationId: debouncedSelectedLocationId ?? undefined,
          });
        }
        if (cancelled) return;
        applyWeekReportData(data, {
          setSchedules,
          setShifts,
          setTimecards,
          setPayRecords,
          setEmployees,
        });
        setLoadingProgress(100);
        setLoadingStage('Complete');
      } catch (err) {
        console.error('Error loading Plan vs Actual report:', err);
        if (!cancelled) {
          setError('Unable to load report data. Please try again.');
        }
      } finally {
        if (progressTimer) clearInterval(progressTimer);
        if (!cancelled) setLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [apiWeekStart, weekStart, weekEnd, debouncedSelectedLocationId]);

  const employeePayRecords = useMemo(() => buildActivePayRecordMap(payRecords), [payRecords]);

  /** Schedule IDs that have at least one shift in the loaded week (and location when selected). */
  const scheduleIdsWithShiftsThisWeek = useMemo(
    () => new Set(shifts.map(s => s.scheduleId)),
    [shifts]
  );

  // Schedules that have at least one shift for this week (and for selected location when set — shifts are already filtered by location).
  const scheduleOptions = useMemo(() => {
    const withShifts = schedules.filter(schedule => scheduleIdsWithShiftsThisWeek.has(schedule.id));
    return [...withShifts].sort((a, b) => a.name.localeCompare(b.name));
  }, [schedules, scheduleIdsWithShiftsThisWeek]);

  useEffect(() => {
    if (
      selectedScheduleId !== 'all' &&
      selectedScheduleId !== 'unscheduled' &&
      typeof selectedScheduleId === 'number' &&
      !scheduleOptions.some(s => s.id === selectedScheduleId)
    ) {
      setSelectedScheduleId('all');
    }
  }, [scheduleOptions, selectedScheduleId]);

  useEffect(() => {
    if (allSchedulesBreakdown && selectedScheduleId !== 'all') {
      setSelectedScheduleId('all');
    }
  }, [allSchedulesBreakdown]);

  const selectedSchedule = useMemo(
    () => schedules.find(schedule => schedule.id === selectedScheduleId) || null,
    [schedules, selectedScheduleId]
  );
  const selectedScheduleCode = selectedSchedule ? getTwoLetterCode(selectedSchedule.name) : null;
  const allowedScheduleCodes = useMemo(() => {
    return new Set(scheduleOptions.map(schedule => getTwoLetterCode(schedule.name)));
  }, [scheduleOptions]);

  const filteredTimecards = useMemo(() => {
    return timecards.filter(timecard => {
      const employee = employees.find(emp => emp.id === timecard.employeeId);
      if (!employee) return false;
      if (debouncedSelectedLocationId && getEmployeeLocationId(employee) !== debouncedSelectedLocationId) {
        return false;
      }
      return true;
    });
  }, [timecards, employees, debouncedSelectedLocationId]);

  /** Timecards that are approved — use for actual hours so report reflects confirmed hours only. */
  const approvedTimecards = useMemo(
    () => filteredTimecards.filter(tc => tc.isApproved === true),
    [filteredTimecards]
  );

  /** Timecards that are not approved — for showing unapproved actual hours. */
  const unapprovedTimecards = useMemo(
    () => filteredTimecards.filter(tc => tc.isApproved !== true),
    [filteredTimecards]
  );

  /** Unapproved actual cost (for display under Actual when Unapproved Timecard hours shown). */
  const unapprovedActualCost = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    if (selectedScheduleId === 'unscheduled') {
      return unapprovedTimecards.reduce((total, timecard) => {
        const pr = employeePayRecords[timecard.employeeId];
        return total + timecard.days.reduce((sum, day) => {
          const dayDate = day.date ? parseISO(day.date) : null;
          if (!dayDate || !isWithinInterval(dayDate, interval) || !isDayUnScheduled(day)) return sum;
          return sum + timecardDayCost(pr, day, timecard.locationId);
        }, 0);
      }, 0);
    }
    return unapprovedTimecards.reduce((total, timecard) => {
      const pr = employeePayRecords[timecard.employeeId];
      return total + timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return sum;
        const scheduleCode = day.scheduleCode ?? '';
        if (selectedScheduleId !== 'all') {
          if (scheduleCode !== selectedScheduleCode) return sum;
        } else if (
          debouncedSelectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + timecardDayCost(pr, day, timecard.locationId);
      }, 0);
    }, 0);
  }, [unapprovedTimecards, weekStart, weekEnd, employeePayRecords, selectedScheduleId, selectedScheduleCode, debouncedSelectedLocationId, allowedScheduleCodes]);

  /** Total actual hours from unapproved timecards (same filters as actual: week range, location, schedule). */
  const unapprovedActualHours = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    if (selectedScheduleId === 'unscheduled') {
      return unapprovedTimecards.reduce((total, timecard) => {
        const timecardTotal = timecard.days.reduce((dayTotal, day) => {
          const dayDate = day.date ? parseISO(day.date) : null;
          if (!dayDate || !isWithinInterval(dayDate, interval)) return dayTotal;
          if (!isDayUnScheduled(day)) return dayTotal;
          return dayTotal + getActualPaidHours(day);
        }, 0);
        return total + timecardTotal;
      }, 0);
    }
    return unapprovedTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((dayTotal, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return dayTotal;
        const scheduleCode = day.scheduleCode ?? '';
        if (selectedScheduleId !== 'all') {
          if (scheduleCode !== selectedScheduleCode) return dayTotal;
        } else if (
          debouncedSelectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return dayTotal;
        }
        return dayTotal + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [unapprovedTimecards, weekStart, weekEnd, selectedScheduleId, selectedScheduleCode, debouncedSelectedLocationId, allowedScheduleCodes]);

  // Plan hours from published shifts (so plan shows even when timecards aren't synced)
  const filteredShifts = useMemo(() => {
    return shifts.filter(shift => {
      const employee = employees.find(emp => emp.id === shift.employeeId);
      if (debouncedSelectedLocationId && (!employee || getEmployeeLocationId(employee) !== debouncedSelectedLocationId)) {
        return false;
      }
      return true;
    });
  }, [shifts, employees, debouncedSelectedLocationId]);

  const planHoursFromShiftsAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return filteredShifts.reduce((total, shift) => {
      try {
        const shiftStart = parseISO(shift.startTime);
        if (!isWithinInterval(shiftStart, interval)) return total;
      } catch {
        return total;
      }
      return total + getShiftHours(shift);
    }, 0);
  }, [filteredShifts, weekStart, weekEnd]);

  const planHoursFromShiftsSelected = useMemo(() => {
    if (selectedScheduleId === 'all') return planHoursFromShiftsAll;
    return filteredShifts
      .filter(shift => shift.scheduleId === selectedScheduleId)
      .reduce((total, shift) => {
        try {
          const shiftStart = parseISO(shift.startTime);
          if (!isWithinInterval(shiftStart, { start: weekStart, end: weekEnd })) return total;
        } catch {
          return total;
        }
        return total + getShiftHours(shift);
      }, 0);
  }, [filteredShifts, selectedScheduleId, planHoursFromShiftsAll, weekStart, weekEnd]);

  // Fallback: plan from timecard days (scheduled in/out) when no shifts returned
  const planHoursAll = useMemo(() => {
    const fromShifts = planHoursFromShiftsAll;
    if (fromShifts > 0) return fromShifts;
    const interval = { start: weekStart, end: weekEnd };
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return sum;
        }
        const scheduleCode = day.scheduleCode ?? '';
        if (
          debouncedSelectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + getScheduledHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [planHoursFromShiftsAll, filteredTimecards, weekStart, weekEnd, debouncedSelectedLocationId, allowedScheduleCodes]);

  const planHoursSelected = useMemo(() => {
    if (selectedScheduleId === 'all') {
      return planHoursAll;
    }
    const fromShifts = planHoursFromShiftsSelected;
    if (fromShifts > 0) return fromShifts;
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        if (!selectedScheduleCode) return sum;
        if ((day.scheduleCode ?? '') !== selectedScheduleCode) {
          return sum;
        }
        return sum + getScheduledHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [filteredTimecards, selectedScheduleId, selectedScheduleCode, planHoursAll, planHoursFromShiftsSelected]);

  const actualHoursAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return sum;
        }
        const scheduleCode = day.scheduleCode ?? '';
        // When a location is selected, only filter by schedule code if the day actually has a schedule code.
        // Days with null/empty scheduleCode but valid totalHours should still be counted.
        if (
          debouncedSelectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [approvedTimecards, weekStart, weekEnd, debouncedSelectedLocationId, allowedScheduleCodes]);

  const actualHoursSelected = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((dayTotal, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return dayTotal;
        }
        if (selectedScheduleId !== 'all') {
          if ((day.scheduleCode ?? '') !== selectedScheduleCode) {
            return dayTotal;
          }
        }
        return dayTotal + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [approvedTimecards, selectedScheduleId, selectedScheduleCode, weekStart, weekEnd]);

  /** Per-schedule plan hours and cost for segment ring (when All Schedules Breakdown is on). */
  const planSegments = useMemo((): CircleSegment[] => {
    const interval = { start: weekStart, end: weekEnd };
    return scheduleOptions
      .map((schedule, idx) => {
        const scheduleShifts = filteredShifts.filter(shift => shift.scheduleId === schedule.id);
        const hours = scheduleShifts.reduce((sum, shift) => {
          try {
            if (!isWithinInterval(parseISO(shift.startTime), interval)) return sum;
          } catch {
            return sum;
          }
          return sum + getShiftHours(shift);
        }, 0);
        const cost = scheduleShifts.reduce((sum, shift) => {
          try {
            if (!isWithinInterval(parseISO(shift.startTime), interval)) return sum;
          } catch {
            return sum;
          }
          return sum + getShiftLaborCost(shift, employeePayRecords);
        }, 0);
        return { scheduleId: schedule.id, name: schedule.name, hours, cost, color: SCHEDULE_SEGMENT_COLORS[idx % SCHEDULE_SEGMENT_COLORS.length] };
      })
      .filter(seg => seg.hours > 0)
      .sort((a, b) => b.hours - a.hours);
  }, [scheduleOptions, filteredShifts, weekStart, weekEnd, employeePayRecords]);

  /** Per-schedule actual hours and cost for segment ring (when All Schedules Breakdown is on). */
  const actualSegments = useMemo((): CircleSegment[] => {
    const interval = { start: weekStart, end: weekEnd };
    return scheduleOptions
      .map((schedule, idx) => {
        const code = getTwoLetterCode(schedule.name);
        let hours = 0;
        let cost = 0;
        approvedTimecards.forEach(timecard => {
          const pr = employeePayRecords[timecard.employeeId];
          timecard.days.forEach(day => {
            const dayDate = day.date ? parseISO(day.date) : null;
            if (!dayDate || !isWithinInterval(dayDate, interval)) return;
            if ((day.scheduleCode ?? '') !== code) return;
            if (debouncedSelectedLocationId && allowedScheduleCodes.size > 0 && code && !allowedScheduleCodes.has(code)) return;
            const h = getActualPaidHours(day);
            hours += h;
            cost += timecardDayCost(pr, day, timecard.locationId);
          });
        });
        return { scheduleId: schedule.id, name: schedule.name, hours, cost, color: SCHEDULE_SEGMENT_COLORS[idx % SCHEDULE_SEGMENT_COLORS.length] };
      })
      .filter(seg => seg.hours > 0)
      .sort((a, b) => b.hours - a.hours);
  }, [scheduleOptions, approvedTimecards, weekStart, weekEnd, debouncedSelectedLocationId, allowedScheduleCodes, employeePayRecords]);

  /** Plan cost (same logic as scheduling: shift labor cost using pay records). */
  const planCostAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return filteredShifts.reduce((total, shift) => {
      try {
        if (!isWithinInterval(parseISO(shift.startTime), interval)) return total;
      } catch {
        return total;
      }
      return total + getShiftLaborCost(shift, employeePayRecords);
    }, 0);
  }, [filteredShifts, weekStart, weekEnd, employeePayRecords]);

  const planCostSelected = useMemo(() => {
    if (selectedScheduleId === 'all') return planCostAll;
    return filteredShifts
      .filter(s => s.scheduleId === selectedScheduleId)
      .reduce((total, shift) => {
        try {
          if (!isWithinInterval(parseISO(shift.startTime), { start: weekStart, end: weekEnd })) return total;
        } catch {
          return total;
        }
        return total + getShiftLaborCost(shift, employeePayRecords);
      }, 0);
  }, [filteredShifts, selectedScheduleId, planCostAll, weekStart, weekEnd, employeePayRecords]);

  /** Actual cost: approved timecard days × employee hourly rate (same schedule/location filter as actual hours). */
  const actualCostAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const pr = employeePayRecords[timecard.employeeId];
      const timecardCost = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return sum;
        const scheduleCode = day.scheduleCode ?? '';
        if (
          debouncedSelectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + timecardDayCost(pr, day, timecard.locationId);
      }, 0);
      return total + timecardCost;
    }, 0);
  }, [approvedTimecards, weekStart, weekEnd, employeePayRecords, debouncedSelectedLocationId, allowedScheduleCodes]);

  const actualCostSelected = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const pr = employeePayRecords[timecard.employeeId];
      const timecardCost = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return sum;
        if (selectedScheduleId !== 'all') {
          if ((day.scheduleCode ?? '') !== selectedScheduleCode) return sum;
        }
        return sum + timecardDayCost(pr, day, timecard.locationId);
      }, 0);
      return total + timecardCost;
    }, 0);
  }, [approvedTimecards, selectedScheduleId, selectedScheduleCode, weekStart, weekEnd, employeePayRecords]);

  /** Actual cost for unscheduled hours only. */
  const actualCostUnScheduled = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const pr = employeePayRecords[timecard.employeeId];
      const timecardCost = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return sum;
        if (!isDayUnScheduled(day)) return sum;
        return sum + timecardDayCost(pr, day, timecard.locationId);
      }, 0);
      return total + timecardCost;
    }, 0);
  }, [approvedTimecards, weekStart, weekEnd, employeePayRecords]);

  /** Actual hours from approved timecard days that have no shift associated (UnScheduled). */
  const actualHoursUnScheduled = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return approvedTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) return sum;
        if (!isDayUnScheduled(day)) return sum;
        return sum + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [approvedTimecards, weekStart, weekEnd]);

  const dailyTotals = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
    const isUnScheduled = selectedScheduleId === 'unscheduled';
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      let plan: number;
      let actual: number;
      let planCost: number;
      let actualCost: number;
      if (isUnScheduled) {
        plan = 0;
        planCost = 0;
        actual = approvedTimecards.reduce((total, timecard) => {
          const dayTotal = timecard.days.reduce((sum, dayEntry) => {
            if (!dayEntry.date) return sum;
            if (format(parseISO(dayEntry.date), 'yyyy-MM-dd') !== dateStr) return sum;
            if (!isDayUnScheduled(dayEntry)) return sum;
            return sum + getActualPaidHours(dayEntry);
          }, 0);
          return total + dayTotal;
        }, 0);
        actualCost = approvedTimecards.reduce((total, timecard) => {
          const pr = employeePayRecords[timecard.employeeId];
          return total + timecard.days.reduce((sum, dayEntry) => {
            if (!dayEntry.date) return sum;
            if (format(parseISO(dayEntry.date), 'yyyy-MM-dd') !== dateStr) return sum;
            if (!isDayUnScheduled(dayEntry)) return sum;
            return sum + timecardDayCost(pr, dayEntry, timecard.locationId);
          }, 0);
        }, 0);
      } else {
        const dayShifts = filteredShifts.filter(shift => {
          if (selectedScheduleId !== 'all' && shift.scheduleId !== selectedScheduleId) return false;
          try {
            return format(parseISO(shift.startTime), 'yyyy-MM-dd') === dateStr;
          } catch {
            return false;
          }
        });
        plan = dayShifts.reduce((sum, shift) => sum + getShiftHours(shift), 0);
        planCost = dayShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift, employeePayRecords), 0);
        if (plan === 0) {
          plan = filteredTimecards.reduce((total, timecard) => {
            const dayTotal = timecard.days.reduce((sum, dayEntry) => {
              if (!dayEntry.date) return sum;
              const dayDateStr = format(parseISO(dayEntry.date), 'yyyy-MM-dd');
              if (dayDateStr !== dateStr) return sum;
              if (selectedScheduleId !== 'all') {
                if ((dayEntry.scheduleCode ?? '') !== selectedScheduleCode) return sum;
              } else if (
                debouncedSelectedLocationId &&
                allowedScheduleCodes.size > 0 &&
                dayEntry.scheduleCode &&
                !allowedScheduleCodes.has(dayEntry.scheduleCode)
              ) {
                return sum;
              }
              return sum + getScheduledHours(dayEntry);
            }, 0);
            return total + dayTotal;
          }, 0);
        }
        actual = approvedTimecards.reduce((total, timecard) => {
          const dayTotal = timecard.days.reduce((sum, dayEntry) => {
            if (!dayEntry.date) return sum;
            const dayDateStr = format(parseISO(dayEntry.date), 'yyyy-MM-dd');
            if (dayDateStr !== dateStr) return sum;
            if (selectedScheduleId !== 'all') {
              if ((dayEntry.scheduleCode ?? '') !== selectedScheduleCode) return sum;
            } else if (
              debouncedSelectedLocationId &&
              allowedScheduleCodes.size > 0 &&
              dayEntry.scheduleCode &&
              !allowedScheduleCodes.has(dayEntry.scheduleCode)
            ) {
              return sum;
            }
            return sum + getActualPaidHours(dayEntry);
          }, 0);
          return total + dayTotal;
        }, 0);
        actualCost = approvedTimecards.reduce((total, timecard) => {
          const pr = employeePayRecords[timecard.employeeId];
          return total + timecard.days.reduce((sum, dayEntry) => {
            if (!dayEntry.date) return sum;
            const dayDateStr = format(parseISO(dayEntry.date), 'yyyy-MM-dd');
            if (dayDateStr !== dateStr) return sum;
            if (selectedScheduleId !== 'all') {
              if ((dayEntry.scheduleCode ?? '') !== selectedScheduleCode) return sum;
            } else if (
              debouncedSelectedLocationId &&
              allowedScheduleCodes.size > 0 &&
              dayEntry.scheduleCode &&
              !allowedScheduleCodes.has(dayEntry.scheduleCode)
            ) {
              return sum;
            }
            return sum + timecardDayCost(pr, dayEntry, timecard.locationId);
          }, 0);
        }, 0);
      }
      return { date: day, dateStr, plan, actual, planCost, actualCost };
    });
  }, [weekStart, filteredShifts, filteredTimecards, approvedTimecards, selectedScheduleId, selectedScheduleCode, debouncedSelectedLocationId, allowedScheduleCodes, employeePayRecords]);

  const planDisplayHours =
    selectedScheduleId === 'unscheduled' ? 0 : selectedScheduleId === 'all' ? planHoursAll : planHoursSelected;
  const actualDisplayHours =
    selectedScheduleId === 'unscheduled' ? actualHoursUnScheduled : selectedScheduleId === 'all' ? actualHoursAll : actualHoursSelected;
  const planCostDisplay =
    selectedScheduleId === 'unscheduled' ? 0 : selectedScheduleId === 'all' ? planCostAll : planCostSelected;
  const actualCostDisplay =
    selectedScheduleId === 'unscheduled'
      ? actualCostUnScheduled
      : selectedScheduleId === 'all'
        ? actualCostAll
        : actualCostSelected;
  const overHours = actualDisplayHours - planDisplayHours;

  return (
    <div className="w-full max-w-[95%] mx-auto p-6 py-8 h-full flex flex-col min-h-0">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900">Plan vs Actual</h1>
              <p className="text-sm text-neutral-500">
                Week of {formatCompanyDate(weekStart)} – {formatCompanyDate(weekEnd)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-neutral-700">Schedules</label>
                <select
                  value={String(selectedScheduleId)}
                  disabled={isBusy || allSchedulesBreakdown}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === 'all') {
                      setSelectedScheduleId('all');
                    } else if (value === 'unscheduled') {
                      setSelectedScheduleId('unscheduled');
                      setAllSchedulesBreakdown(false);
                    } else {
                      setSelectedScheduleId(Number(value));
                      setAllSchedulesBreakdown(false);
                    }
                  }}
                  className="w-52 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="all">All Schedules</option>
                  {scheduleOptions.map(schedule => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </option>
                  ))}
                  <option value="unscheduled">UnScheduled</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSchedulesBreakdown}
                  disabled={isBusy}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setAllSchedulesBreakdown(on);
                    if (on) setSelectedScheduleId('all');
                  }}
                  className="w-4 h-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-sm font-medium text-neutral-700">All Schedules Breakdown</span>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <WeekNavigator
              weekStart={weekStart}
              weekEnd={weekEnd}
              weekStartsOn={weekStartsOn}
              currentWeek={currentWeek}
              onPreviousWeek={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              onNextWeek={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              onCurrentWeek={() => setCurrentWeek(new Date())}
              onWeekSelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
              disabled={isBusy}
            />
          </div>
        </div>

        {isBusy ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center w-full max-w-md mx-auto px-4">
              <p className="text-sm font-medium text-neutral-700 mb-2">{loadingStage}</p>
              <div className="w-full bg-neutral-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <p className="text-2xl font-semibold text-neutral-900 mt-3">{loadingProgress}%</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 w-full lg:w-3/5 mx-auto">
            {overHours > 0 && (
              <div className="flex justify-end mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-sm font-semibold border border-rose-100">
                  ⚡ {Math.round(overHours)}h Over
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <CircleCard
                title="Plan"
                hours={planDisplayHours}
                cost={Math.round(planCostDisplay * 100) / 100}
                subtitle="Planned Hours"
                totalHours={!allSchedulesBreakdown && selectedScheduleId !== 'all' && selectedScheduleId !== 'unscheduled' ? planHoursAll : undefined}
                highlightHours={!allSchedulesBreakdown && selectedScheduleId !== 'all' && selectedScheduleId !== 'unscheduled' ? planHoursSelected : undefined}
                lightColor="#bfdbfe"
                darkColor="#60a5fa"
                segments={allSchedulesBreakdown && selectedScheduleId === 'all' ? planSegments : undefined}
              />
              <div className="flex flex-col items-center gap-2">
                <CircleCard
                  title="Actual"
                  hours={actualDisplayHours}
                  cost={Math.round(actualCostDisplay * 100) / 100}
                  subtitle="Actual Hours"
                  totalHours={!allSchedulesBreakdown && selectedScheduleId !== 'all' && selectedScheduleId !== 'unscheduled' ? actualHoursAll : undefined}
                  highlightHours={!allSchedulesBreakdown && selectedScheduleId !== 'all' && selectedScheduleId !== 'unscheduled' ? actualHoursSelected : undefined}
                  lightColor="#bbf7d0"
                  darkColor="#34d399"
                  segments={allSchedulesBreakdown && selectedScheduleId === 'all' ? actualSegments : undefined}
                />
                {unapprovedActualHours > 0 && (
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-800 text-sm font-medium border border-amber-200">
                    Unapproved Timecard hours: <strong>{unapprovedActualHours.toFixed(2)}h</strong>
                    {' '}(€{(Math.round(unapprovedActualCost * 100) / 100).toFixed(2)})
                  </span>
                )}
              </div>
            </div>
            <div className="mt-8 border-t border-neutral-200 pt-6">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="text-sm font-semibold text-neutral-700">Daily breakdown</div>
                <div className="flex items-center gap-4 text-xs text-neutral-500">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-4 rounded-sm bg-[#60a5fa]" />
                    Planned ≥ Actual
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-4 rounded-sm bg-[#34d399]" />
                    Actual &gt; Plan
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                {dailyTotals.map(day => {
                  const isPlanOver = day.plan >= day.actual;
                  const barColor = isPlanOver ? 'bg-[#60a5fa]' : 'bg-[#34d399]';
                  return (
                    <div
                      key={day.dateStr}
                      className="relative flex flex-col items-center gap-2"
                      onMouseEnter={() => setHoveredDay(day.dateStr)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      {hoveredDay === day.dateStr && (
                        <div className="absolute -top-24 z-10 w-72 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm shadow-lg">
                          <div className="text-base font-semibold text-neutral-800">
                            {format(day.date, 'EEEE')}, {formatCompanyDate(day.date)}
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-neutral-700">
                            <span className="text-base" aria-hidden>📅</span>
                            <span className="font-medium">Planned:</span>
                            <span>{day.plan.toFixed(2)}h</span>
                            <span className="text-neutral-500">(€{day.planCost.toFixed(2)})</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-neutral-700">
                            <span className="text-base" aria-hidden>🕒</span>
                            <span className="font-medium">Actual:</span>
                            <span>{day.actual.toFixed(2)}h</span>
                            <span className="text-neutral-500">(€{day.actualCost.toFixed(2)})</span>
                          </div>
                        </div>
                      )}
                      <div className="w-full max-w-[90px]">
                        <div className={`h-4 rounded ${barColor}`} />
                      </div>
                      <div className="text-xs text-neutral-500 text-center">
                        {formatCompanyDate(day.date)}
                      </div>
                      <div className="text-xs text-neutral-400 text-center">
                        €{day.planCost.toFixed(0)} / €{day.actualCost.toFixed(0)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanVsActual;
